import { useState, useEffect, useMemo } from 'react';
import { apiGet, apiPost, apiDownload, getPlanInfo } from '../api/client';
import type { Invoice, PlanInfo } from '../types';
import { canManagePlan, openPlanManagement, planManagementButtonLabel } from '../utils/billing';
import { formatDate } from '../utils/date';
import { formatMoney } from '../utils/currency';
import { canCancelInvoice, canCreateCreditNote, getEffectiveInvoiceStatus, getPrimaryReversalDocument, isFullyReversed } from '../utils/invoiceStatus';
import { invoiceOrderLabel } from '../utils/order';
import { t } from '../i18n';
import { Modal } from '../components/Modal';

interface InvoicesProps {
  onError: (error: string | null) => void;
}

const PAGE_SIZES = [5, 10, 20];

export function Invoices({ onError }: InvoicesProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [issuing, setIssuing] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [expandedRelatedIds, setExpandedRelatedIds] = useState<Set<number>>(() => new Set());

  const [cancelModal, setCancelModal] = useState<Invoice | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const [creditModal, setCreditModal] = useState<Invoice | null>(null);
  const [creditType, setCreditType] = useState<'full' | 'partial'>('full');
  const [creditItems, setCreditItems] = useState<Array<{
    invoice_item_id: number;
    description: string;
    unit_price: string;
    tax_rate: string;
    original_quantity: number;
    remaining_quantity: number;
    selected_quantity: number;
    selected: boolean;
  }>>([]);
  const [creditReason, setCreditReason] = useState('');
  const [crediting, setCrediting] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadInvoices();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [pageSize, statusFilter, search]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    const rowsWithDraftRelatedDocs = invoices
      .filter(invoice => (invoice.credit_notes || []).some(doc => doc.status === 'draft'))
      .map(invoice => invoice.id);

    if (rowsWithDraftRelatedDocs.length === 0) return;

    setExpandedRelatedIds(prev => {
      const next = new Set(prev);
      rowsWithDraftRelatedDocs.forEach(id => next.add(id));
      return next;
    });
  }, [invoices]);

  async function loadInvoices() {
    setLoading(true);
    try {
      const [data, plan] = await Promise.all([
        apiGet<Invoice[]>('/invoices'),
        getPlanInfo(),
      ]);
      setInvoices(data || []);
      setPlanInfo(plan);
      onError(null);
    } catch {
      onError(t('invoices_load_error'));
    } finally {
      setLoading(false);
    }
  }

  async function handleIssue(invoiceId: number) {
    setIssuing(invoiceId);
    try {
      await apiPost(`/invoices/${invoiceId}/issue`);
      await loadInvoices();
      onError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('unknown_error');
      if (message.includes('invoice_limit_reached')) {
        onError(t('monthly_limit_error'));
      } else {
        onError(message);
      }
    } finally {
      setIssuing(null);
    }
  }

  async function handleDownload(invoiceId: number, format: 'pdf' | 'zugferd' | 'xrechnung') {
    try {
      const ext = format === 'pdf' ? 'pdf' : 'xml';
      const inv = invoices.find(i => i.id === invoiceId);
      const name = inv ? `${inv.invoice_number}.${ext}` : `${format}.${ext}`;
      await apiDownload(`/invoices/${invoiceId}/download/${format}`, name);
    } catch (err) {
      onError(t('download_error') + ' ' + (err instanceof Error ? err.message : t('unknown_error')));
    }
  }

  function openCancelModal(invoice: Invoice) {
    setCancelModal(invoice);
    setCancelReason('');
  }

  async function handleCancel() {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      await apiPost(`/invoices/${cancelModal.id}/cancel`, {
        cancellation_reason: cancelReason || undefined,
      });
      setExpandedRelatedIds(prev => new Set(prev).add(cancelModal.id));
      setCancelModal(null);
      await loadInvoices();
      setSuccessMessage(t('cancel_invoice_success'));
      onError(null);
    } catch (err) {
      onError(t('cancel_invoice_error') + ' ' + (err instanceof Error ? err.message : t('unknown_error')));
    } finally {
      setCancelling(false);
    }
  }

  function openCreditModal(invoice: Invoice) {
    setCreditModal(invoice);
    setCreditType('full');
    setCreditReason('');
    setCreditItems([]);
  }

  async function loadCreditableItems() {
    if (!creditModal) return;
    try {
      const data = await apiGet<Array<{
        id: number;
        description: string;
        unit_price: string;
        tax_rate: string;
        original_quantity: number;
        remaining_quantity: number;
        platform_line_item_id: string | null;
      }>>(`/invoices/${creditModal.id}/creditable-items`);
      setCreditItems(data.map(item => ({
        invoice_item_id: item.id,
        description: item.description,
        unit_price: item.unit_price,
        tax_rate: item.tax_rate,
        original_quantity: item.original_quantity,
        remaining_quantity: item.remaining_quantity,
        selected_quantity: item.remaining_quantity,
        selected: true,
      })));
    } catch {
      onError(t('credit_note_error'));
    }
  }

  useEffect(() => {
    if (creditModal && creditType === 'partial') {
      loadCreditableItems();
    }
  }, [creditModal, creditType]);

  async function handleCreditNote() {
    if (!creditModal) return;
    setCrediting(true);
    try {
      const body: Record<string, unknown> = {
        type: creditType,
        reason: creditReason || undefined,
      };
      if (creditType === 'partial') {
        body.items = creditItems
          .filter(item => item.selected && item.selected_quantity > 0)
          .map(item => ({
            invoice_item_id: item.invoice_item_id,
            quantity: item.selected_quantity,
          }));
      }
      await apiPost(`/invoices/${creditModal.id}/credit-note`, body);
      setExpandedRelatedIds(prev => new Set(prev).add(creditModal.id));
      setCreditModal(null);
      await loadInvoices();
      setSuccessMessage(t('credit_note_success'));
      onError(null);
    } catch (err) {
      onError(t('credit_note_error') + ' ' + (err instanceof Error ? err.message : t('unknown_error')));
    } finally {
      setCrediting(false);
    }
  }

  const filtered = useMemo(() => {
    let result = invoices.filter(i => !i.original_invoice_id);
    if (statusFilter !== 'all') {
      result = result.filter(i => getEffectiveInvoiceStatus(i) === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(i =>
        i.invoice_number.toLowerCase().includes(q) ||
        (i.platform_order_name && i.platform_order_name.toLowerCase().includes(q)) ||
        (i.platform_order_id && i.platform_order_id.toLowerCase().includes(q)) ||
        (i.customer?.company_name && i.customer.company_name.toLowerCase().includes(q)) ||
        (i.customer?.email && i.customer.email.toLowerCase().includes(q)) ||
        (i.credit_notes && i.credit_notes.some(c => c.invoice_number.toLowerCase().includes(q)))
      );
    }
    return result;
  }, [invoices, statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const canIssue = planInfo ? planInfo.documents_remaining > 0 : true;
  const hasCreditNotes = planInfo?.features.credit_notes ?? false;
  const hasCancellations = planInfo?.features.cancellations ?? false;

  return (
    <div className="page-container">
      <div className="section-header">
        <h1 className="section-title">{t('invoices_title')}</h1>
        <button onClick={loadInvoices} disabled={loading} className="btn btn--secondary">{t('refresh')}</button>
      </div>

      {successMessage && (
        <div className="banner banner--success mb-4">
          <span className="banner-content">{successMessage}</span>
        </div>
      )}

      {planInfo && (planInfo.can_manage_plan ?? Boolean(planInfo.pricing_url)) && (
        <div className="card mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm" style={{ color: planInfo.documents_remaining <= 0 ? 'var(--color-critical)' : 'var(--color-text-secondary)' }}>
              {planInfo.documents_remaining <= 0
                ? t('plan_limit_reached', { limit: planInfo.document_limit })
                : t('documents_used', { used: planInfo.documents_used, limit: planInfo.document_limit })}
            </span>
            <button
              type="button"
              onClick={() => openPlanManagement(planInfo)}
              disabled={!canManagePlan(planInfo)}
              title={!canManagePlan(planInfo) ? t('manage_plan_tooltip') : undefined}
              className="btn btn--primary btn--sm"
            >
              {planManagementButtonLabel()}
            </button>
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="flex items-center gap-3 mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('search_placeholder')}
            className="form-input"
            style={{ maxWidth: 400 }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input form-select"
            style={{ maxWidth: 180 }}
          >
            <option value="all">{t('filter_all')}</option>
            <option value="draft">{t('draft')}</option>
            <option value="issued">{t('issued')}</option>
            <option value="cancelled">{t('status_cancelled')}</option>
          </select>
        </div>

        {loading ? (
          <LoadingSkeleton />
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">
              {invoices.length === 0 ? t('no_invoices_empty') : t('no_invoices_filter')}
            </div>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>{t('col_invoice')}</th>
                    <th>{t('col_order')}</th>
                    <th>{t('col_customer')}</th>
                    <th>{t('col_date')}</th>
                    <th>{t('col_total')}</th>
                    <th>{t('col_status')}</th>
                    <th>{t('col_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((inv) => (
                    <InvoiceRow
                      key={inv.id}
                      invoice={inv}
                      canIssue={canIssue}
                      hasCreditNotes={hasCreditNotes}
                      hasCancellations={hasCancellations}
                      issuing={issuing}
                      expanded={expandedRelatedIds.has(inv.id)}
                      onToggleRelated={(id) => {
                        setExpandedRelatedIds(prev => {
                          const next = new Set(prev);
                          if (next.has(id)) {
                            next.delete(id);
                          } else {
                            next.add(id);
                          }
                          return next;
                        });
                      }}
                      onIssue={handleIssue}
                      onDownload={handleDownload}
                      onCancel={openCancelModal}
                      onCreditNote={openCreditModal}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              total={filtered.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>

      <Modal
        open={!!cancelModal}
        onClose={() => setCancelModal(null)}
        title={t('cancel_invoice')}
        footer={
          <>
            <button onClick={() => setCancelModal(null)} className="btn btn--secondary">{t('hide')}</button>
            <button onClick={handleCancel} disabled={cancelling} className="btn btn--danger">
              {cancelling ? t('creating') : t('cancel_invoice')}
            </button>
          </>
        }
      >
        <p style={{ marginBottom: 'var(--space-4)' }}>{t('cancel_invoice_confirm')}</p>
        <p className="text-sm text-secondary" style={{ marginBottom: 'var(--space-4)' }}>{t('cancel_invoice_explanation')}</p>
        {cancelModal && (
          <div className="card mb-4" style={{ padding: 'var(--space-3)', background: 'var(--color-surface-secondary)' }}>
            <div className="text-sm"><strong>{cancelModal.invoice_number}</strong></div>
            <div className="text-sm text-secondary">{formatMoney(cancelModal.total, cancelModal.currency)}</div>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">{t('cancellation_reason_placeholder')}</label>
          <input
            type="text"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('cancellation_reason_placeholder')}
            className="form-input"
          />
        </div>
      </Modal>

      <Modal
        open={!!creditModal}
        onClose={() => setCreditModal(null)}
        title={t('create_credit_note')}
        footer={
          <>
            <button onClick={() => setCreditModal(null)} className="btn btn--secondary">{t('hide')}</button>
            <button
              onClick={handleCreditNote}
              disabled={crediting || (creditType === 'partial' && !creditItems.some(i => i.selected))}
              className="btn btn--primary"
            >
              {crediting ? t('creating') : t('create_credit_note')}
            </button>
          </>
        }
      >
        {creditModal && (
          <>
            <div className="card mb-4" style={{ padding: 'var(--space-3)', background: 'var(--color-surface-secondary)' }}>
              <div className="text-sm"><strong>{creditModal.invoice_number}</strong></div>
              <div className="text-sm text-secondary">{formatMoney(creditModal.total, creditModal.currency)}</div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('credit_note_type')}</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="credit_type"
                    value="full"
                    checked={creditType === 'full'}
                    onChange={() => setCreditType('full')}
                  />
                  {t('credit_note_full')}
                </label>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="credit_type"
                    value="partial"
                    checked={creditType === 'partial'}
                    onChange={() => setCreditType('partial')}
                  />
                  {t('credit_note_partial')}
                </label>
              </div>
              {creditType === 'partial' && (
                <div className="text-sm text-secondary" style={{ marginTop: 'var(--space-2)' }}>
                  {t('credit_note_partial_hint')}
                </div>
              )}
            </div>

            {creditType === 'partial' && creditItems.length > 0 && (
              <div className="form-group">
                <label className="form-label">{t('credit_note_items')}</label>
                <table className="credit-items-table">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}></th>
                      <th>{t('col_invoice')}</th>
                      <th>{t('credit_note_original_qty')}</th>
                      <th>{t('credit_note_remaining')}</th>
                      <th>{t('credit_note_credit_fraction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditItems.map((item, idx) => (
                      <tr key={item.invoice_item_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={item.selected}
                            onChange={(e) => {
                              const updated = [...creditItems];
                              updated[idx].selected = e.target.checked;
                              if (!e.target.checked) updated[idx].selected_quantity = 0;
                              else updated[idx].selected_quantity = updated[idx].remaining_quantity;
                              setCreditItems(updated);
                            }}
                          />
                        </td>
                        <td className="text-sm">{item.description}</td>
                        <td className="text-sm">{item.original_quantity}</td>
                        <td className="text-sm credit-remaining">{item.remaining_quantity}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={item.remaining_quantity}
                            step="0.01"
                            value={item.selected_quantity}
                            disabled={!item.selected}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const updated = [...creditItems];
                              updated[idx].selected_quantity = Math.min(val, updated[idx].remaining_quantity);
                              setCreditItems(updated);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('credit_note_reason')}</label>
              <input
                type="text"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder={t('credit_note_reason')}
                className="form-input"
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function InvoiceRow({ invoice, canIssue, hasCreditNotes, hasCancellations, issuing, expanded, onToggleRelated, onIssue, onDownload, onCancel, onCreditNote }: {
  invoice: Invoice;
  canIssue: boolean;
  hasCreditNotes: boolean;
  hasCancellations: boolean;
  issuing: number | null;
  expanded: boolean;
  onToggleRelated: (id: number) => void;
  onIssue: (id: number) => void;
  onDownload: (id: number, format: 'pdf' | 'zugferd' | 'xrechnung') => void;
  onCancel: (invoice: Invoice) => void;
  onCreditNote: (invoice: Invoice) => void;
}) {
  const relatedDocs = invoice.credit_notes || [];
  const hasRelated = relatedDocs.length > 0;
  const effectiveStatus = getEffectiveInvoiceStatus(invoice);

  return (
    <>
      <tr>
        <td className="fw-medium">
          {invoice.invoice_number}
          {invoice.invoice_type === 'credit_note' && (
            <span className="badge badge--info" style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)' }}>{t('status_credit_note')}</span>
          )}
          {invoice.invoice_type === 'cancellation' && (
            <span className="badge badge--critical" style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)' }}>{t('status_cancellation')}</span>
          )}
          {invoice.original_invoice && (
            <div className="text-sm text-secondary">
              {invoice.invoice_type === 'credit_note' ? t('credit_note_of') : t('cancellation_of')} {invoice.original_invoice.invoice_number}
            </div>
          )}
          {isFullyReversed(invoice) && (() => {
            const reversalDoc = getPrimaryReversalDocument(invoice);
            return reversalDoc ? (
              <div className="text-sm text-secondary">
                {t('cancelled_hint', { number: reversalDoc.invoice_number })}
              </div>
            ) : null;
          })()}
        </td>
        <td>{invoiceOrderLabel(invoice)}</td>
        <td>{invoice.customer?.company_name || [invoice.customer?.first_name, invoice.customer?.last_name].filter(Boolean).join(' ') || '—'}</td>
        <td className="text-secondary">{formatDate(invoice.issue_date)}</td>
        <td className="fw-medium">{invoice.total} {invoice.currency}</td>
        <td><StatusBadge status={effectiveStatus} invoiceType={invoice.invoice_type} /></td>
        <td>
          <div className="flex gap-2">
            {['draft', 'validation_failed'].includes(invoice.status) && (
              <button
                onClick={() => onIssue(invoice.id)}
                disabled={issuing === invoice.id || !canIssue}
                title={!canIssue ? t('document_limit_reached') : undefined}
                className="btn btn--success btn--sm"
              >
                {issuing === invoice.id ? t('issuing') : t('issue')}
              </button>
            )}
            {invoice.status === 'issued' && (
              <>
                <button onClick={() => onDownload(invoice.id, 'pdf')} className="btn btn--secondary btn--sm">PDF</button>
                <button onClick={() => onDownload(invoice.id, 'zugferd')} className="btn btn--secondary btn--sm">XML</button>
                {hasCancellations && canCancelInvoice(invoice) && (
                  <button
                    onClick={() => onCancel(invoice)}
                    className="btn btn--danger btn--sm"
                  >
                    {t('cancel_invoice')}
                  </button>
                )}
                {invoice.invoice_type === 'standard' && hasCreditNotes && canCreateCreditNote(invoice) && (
                  <button
                    onClick={() => onCreditNote(invoice)}
                    className="btn btn--secondary btn--sm"
                  >
                    {t('create_credit_note')}
                  </button>
                )}
              </>
            )}
            {hasRelated && (
              <button
                onClick={() => onToggleRelated(invoice.id)}
                className="btn btn--tertiary btn--sm"
              >
                {relatedDocs.length} {t('related_documents')}
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && relatedDocs.map((doc) => (
        <tr key={doc.id} style={{ background: 'var(--color-surface-secondary)' }}>
          <td className="text-sm" style={{ paddingLeft: 'var(--space-6)' }}>
            <span className="text-secondary">↳</span> {doc.invoice_number}
            <StatusBadge status={doc.status} invoiceType={doc.invoice_type} />
          </td>
          <td></td>
          <td></td>
          <td className="text-secondary text-sm">{formatDate(doc.issue_date)}</td>
          <td className="fw-medium text-sm">{doc.total} {doc.currency}</td>
          <td></td>
          <td>
            <div className="flex gap-2">
              {doc.status === 'draft' && (
                <button
                  onClick={() => onIssue(doc.id)}
                  disabled={issuing === doc.id || !canIssue}
                  title={!canIssue ? t('document_limit_reached') : undefined}
                  className="btn btn--success btn--sm"
                >
                  {issuing === doc.id ? t('issuing') : t('issue')}
                </button>
              )}
              {doc.status === 'issued' && (
                <>
                  <button onClick={() => onDownload(doc.id, 'pdf')} className="btn btn--secondary btn--sm">PDF</button>
                  <button onClick={() => onDownload(doc.id, 'zugferd')} className="btn btn--secondary btn--sm">XML</button>
                </>
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

function Pagination({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
}) {
  return (
    <div className="pagination">
      <span className="flex items-center gap-2">
        {total} {t('entries')}
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="pagination-select"
        >
          {PAGE_SIZES.map(s => <option key={s} value={s}>{s}{' ' + t('per_page')}</option>)}
        </select>
      </span>
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="pagination-btn">{'<'}</button>
          <span className="text-sm">{page} / {totalPages}</span>
          <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="pagination-btn">{'>'}</button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string; invoiceType?: string }) {
  const badgeClass: Record<string, string> = {
    issued: 'success',
    draft: 'warning',
    cancelled: 'critical',
  };
  const labels: Record<string, string> = {
    issued: t('status_issued'),
    draft: t('status_draft'),
    cancelled: t('status_cancelled'),
  };
  return (
    <span className={`badge badge--${badgeClass[status] || 'neutral'}`}>
      {labels[status] || status}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div>
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />
      ))}
    </div>
  );
}
